import { isNexusReasonCode, nexusReasonCodes, type NexusReasonCode } from './nexus-reasons';
import { hubIntegrations } from "../types/integrations";
import type { HubPermission } from "../types/hub";
export type DataState =
  | "REAL_DATA"
  | "NO_DATA"
  | "UNVERIFIED"
  | "DISCONNECTED"
  | "DEGRADED"
  | "ERROR"
  | "PERMISSION_DENIED";
export type Observation<T> =
  | { state: "REAL_DATA"; data: T; observedAt: string; source: string }
  | {
      state: Exclude<DataState, "REAL_DATA">;
      data: null;
      observedAt: string | null;
      source: string;
    };
export function unavailable<T>(
  source: string,
  state: Exclude<DataState, "REAL_DATA"> = "UNVERIFIED",
): Observation<T> {
  return { state, data: null, source, observedAt: null };
}
export type DashboardSource =
  | "holded"
  | "clientify"
  | "metricool"
  | "gsc"
  | "nexus"
  | "hermes"
  | "system"
  | "audit";
export interface DashboardMetric {
  id: string;
  label: string;
  value: number;
  unit: "EUR" | "COUNT" | "PERCENT";
}
export interface DashboardProvider {
  readonly source: DashboardSource;
  read(signal: AbortSignal): Promise<Observation<readonly DashboardMetric[]>>;
}
/** No network request or invented response until a verified provider is supplied. */
export class DisconnectedDashboardProvider implements DashboardProvider {
  constructor(readonly source: DashboardSource) {}
  async read(
    signal: AbortSignal,
  ): Promise<Observation<readonly DashboardMetric[]>> {
    if (signal.aborted) throw new Error("Aborted");
    return unavailable(this.source, "DISCONNECTED");
  }
}
export interface IntegrationSnapshot {
  id: string;
  name: string;
  provider: string;
  category: "BUSINESS" | "INFRASTRUCTURE" | "INTERNAL";
  enabled: boolean;
  configured: boolean;
  connectionStatus: "UNVERIFIED" | "CONNECTED" | "DISCONNECTED";
  healthStatus: "UNVERIFIED" | "HEALTHY" | "DEGRADED" | "ERROR";
  lastCheck: string | null;
  capabilities: readonly string[];
}
export function integrationStage(
  item: IntegrationSnapshot,
): "CATALOGUED" | "CONFIGURED" | "CONNECTED" | "HEALTHY" {
  if (!item.configured) return "CATALOGUED";
  if (item.connectionStatus !== "CONNECTED" || !item.lastCheck)
    return "CONFIGURED";
  return item.healthStatus === "HEALTHY" ? "HEALTHY" : "CONNECTED";
}
export function catalogSnapshot(): IntegrationSnapshot[] {
  return hubIntegrations.map((item) => ({
    id: item.id,
    name: item.name,
    provider: item.id,
    category: item.kind,
    enabled: false,
    configured: false,
    connectionStatus: "UNVERIFIED",
    healthStatus: "UNVERIFIED",
    lastCheck: null,
    capabilities: [],
  }));
}
export interface AgentDefinition {
  id: string;
  name: string;
  role: string;
  status: "RUNNING" | "IDLE" | "READY" | "DISCONNECTED" | "DISABLED" | "ERROR";
  capabilities: readonly string[];
  skills: readonly string[];
  tools: readonly string[];
  integrations: readonly string[];
  lastExecution: string | null;
  executionStatus: "COMPLETED" | "FAILED" | "RUNNING" | null;
}
export interface RuntimeCapability {
  id: string;
  name: string;
  requiredPermission: HubPermission;
}
export interface NexusSnapshot {
  state: "DISCONNECTED" | "REAL_DATA" | "ERROR";
  agents: readonly AgentDefinition[];
  skills: readonly RuntimeCapability[];
  mcp: readonly RuntimeCapability[];
  tools: readonly RuntimeCapability[];
  executions: readonly {
    requestId: string;
    agentId: string;
    status: string;
    timestamp: string;
  }[];
}
export type SafeAuditMetadata = Record<string, string | number | boolean | NexusReasonCode[]>;
export interface UnifiedAuditEvent {
  id: string;
  requestId: string | null;
  actor: { type: "HUMAN" | "AGENT" | "SYSTEM"; id: string };
  action: string;
  resource: string;
  result: string;
  timestamp: string;
  metadata: SafeAuditMetadata;
}
/** Allowlist: arbitrary free text, payloads, credentials and error messages are never logged. */
export function safeAuditMetadata(
  input: Record<string, unknown> = {},
): SafeAuditMetadata {
  const output: SafeAuditMetadata = {};
  if (Array.isArray(input.reasonCodes)) output.reasonCodes = nexusReasonCodes(input.reasonCodes.filter(isNexusReasonCode));
  for (const key of ["durationMs", "count", "attempt"])
    if (typeof input[key] === "number" && Number.isFinite(input[key]))
      output[key] = input[key] as number;
  for (const key of ["requestId", "reasonCode", "provider", "status", "taskCode", "fromUserId"])
    if (
      typeof input[key] === "string" &&
      /^[a-zA-Z0-9_.:/-]{1,128}$/.test(input[key] as string)
    )
      output[key] = input[key] as string;
  // Share Secret audit fields (F-08): safe identifiers, no plaintext
  for (const key of [
    'actorUserId', 'recipientUserId', 'itemId', 'shareId', 'selectedIdentityId',
    'identityKind', 'typedEmail', 'action', 'expiresAt', 'timestamp', 'grantedBy',
  ])
    if (
      typeof input[key] === 'string' &&
      /^[a-zA-Z0-9_.:@\/-]{1,256}$/.test(input[key] as string)
    )
      output[key] = input[key] as string;
  // Marketing approvals (BHM-CDX-DIR-100): decisión e identificador de approval
  // como identificadores seguros (APPROVED | REJECTED | uuid) — nunca el comentario.
  for (const key of ['decision', 'approvalId', 'brandId'])
    if (
      typeof input[key] === 'string' &&
      /^[a-zA-Z0-9_.:@\/-]{1,128}$/.test(input[key] as string)
    )
      output[key] = input[key] as string;
  // Permissions object: serialize to safe string
  if (input['permissions'] && typeof input['permissions'] === 'object') {
    const entries = Object.entries(input['permissions'] as Record<string, unknown>);
    const safe = entries.filter(([, v]) => typeof v === 'boolean').map(([k, v]) => k + ':' + v).join(',');
    if (/^[a-zA-Z0-9_,:.\.-]{0,256}$/.test(safe)) output['permissions'] = safe;
  }
  // Share Secret audit fields (F-08): safe identifiers, no plaintext
  for (const key of [
    "actorUserId", "recipientUserId", "itemId", "shareId", "selectedIdentityId",
    "identityKind", "typedEmail", "action", "expiresAt", "timestamp", "grantedBy",
  ])
    if (
      typeof input[key] === "string" &&
      /^[a-zA-Z0-9_.:@/-]{1,256}$/.test(input[key] as string)
    )
      output[key] = input[key] as string;
  // Permissions: serialize booleans to safe string
  if (input["permissions"] && typeof input["permissions"] === "object") {
    const entries = Object.entries(input["permissions"] as Record<string, unknown>);
    const safe = entries.filter(([, v]) => typeof v === "boolean").map(([k, v]) => `${k}:${v}`).join(",");
    if (/^[a-zA-Z0-9_,:.\-]{0,256}$/.test(safe)) output["permissions"] = safe;
  }
  return output;
}
export interface SystemSnapshot {
  api: "UP";
  db: "UP" | "DOWN";
  version: string;
  uptime: number;
  checkedAt: string;
  nexus: "DISCONNECTED";
  hermes: "DISCONNECTED";
  integrations: IntegrationSnapshot[];
}
